import { FormEvent, Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ORDER_OUTSIDE_ONTARIO_NOTE,
  ORDER_INSTITUTE_TYPES,
  ORDER_ORGANIZATION_HINT,
  ORDER_PROVINCES,
  ORDER_QUANTITY_HINT,
  ORDER_QUANTITY_MAX,
  ORDER_RESOURCES,
  OrderResourceId,
} from '../content/orderResources';
import { resourceOrderAPI } from '../services/resourceOrderApi';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import Breadcrumb from '../components/layout/Breadcrumb';
import { usePageTitle } from '../hooks/usePageTitle';

const emptyQuantities = Object.fromEntries(ORDER_RESOURCES.map((resource) => [resource.id, 0])) as Record<
  OrderResourceId,
  number
>;

export default function OrderResources() {
  usePageTitle('Order Resources');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [instituteType, setInstituteType] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [streetAddress2, setStreetAddress2] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('N/A');
  const [quantities, setQuantities] = useState(emptyQuantities);
  const [acknowledgedLimits, setAcknowledgedLimits] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await resourceOrderAPI.submitOrder({
        firstName,
        lastName,
        email,
        organizationName,
        instituteType,
        streetAddress,
        streetAddress2,
        city,
        province,
        postalCode,
        deliveryInstructions,
        quantities,
        acknowledgedLimits,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Order Resources' }]} />
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Order Resources</h1>

      {submitted ? (
        <p className="text-gray-700">
          Your order has been received.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-8">
          <p className="text-gray-700">
            In an effort to go green, we encourage you to use our{' '}
            <Link to="/resources" className="text-primary hover:text-primary-dark">
              resources
            </Link>
            .
          </p>
          <p className="text-gray-700">{ORDER_OUTSIDE_ONTARIO_NOTE}</p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Quantities</h2>
            <p className="text-sm text-gray-600">{ORDER_QUANTITY_HINT}</p>
            {ORDER_RESOURCES.map((resource) => (
              <Fragment key={resource.id}>
                {resource.id === 'bebaFr' && (
                  <h3 className="text-lg font-semibold text-gray-900 pt-2">French</h3>
                )}
              <Input
                label={resource.title}
                type="number"
                min={0}
                max={ORDER_QUANTITY_MAX}
                step={1}
                value={quantities[resource.id]}
                onChange={(event) => {
                  const parsed = Number.parseInt(event.target.value, 10);
                  const next = Number.isFinite(parsed)
                    ? Math.min(ORDER_QUANTITY_MAX, Math.max(0, parsed))
                    : 0;
                  setQuantities((current) => ({ ...current, [resource.id]: next }));
                }}
              />
              </Fragment>
            ))}
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="First name" value={firstName} onChange={(event) => setFirstName(event.target.value)} required />
              <Input label="Last name" value={lastName} onChange={(event) => setLastName(event.target.value)} required />
            </div>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="ex: myname@example.com"
              required
            />
            <p className="text-sm text-gray-600 -mt-2">example@example.com</p>
            <Input
              label="Hospital or school name"
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              required
            />
            <p className="text-sm text-gray-600 -mt-2">{ORDER_ORGANIZATION_HINT}</p>
            <Select
              label="Institute type"
              value={instituteType}
              onChange={(event) => setInstituteType(event.target.value)}
              required
              options={[
                { value: '', label: 'Select a type' },
                ...ORDER_INSTITUTE_TYPES.map((type) => ({ value: type, label: type })),
              ]}
            />
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Delivery</h2>
            <Input
              label="Street address"
              value={streetAddress}
              onChange={(event) => setStreetAddress(event.target.value)}
              required
            />
            <Input
              label="Street address line 2"
              value={streetAddress2}
              onChange={(event) => setStreetAddress2(event.target.value)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="City" value={city} onChange={(event) => setCity(event.target.value)} required />
              <Select
                label="Province"
                value={province}
                onChange={(event) => setProvince(event.target.value)}
                required
                options={[
                  { value: '', label: 'Select a province' },
                  ...ORDER_PROVINCES.map((name) => ({ value: name, label: name })),
                ]}
              />
            </div>
            <Input
              label="Postal code"
              value={postalCode}
              onChange={(event) => setPostalCode(event.target.value)}
              required
            />
            <div>
              <label htmlFor="delivery-instructions" className="block text-sm font-medium text-gray-700 mb-2">
                Special delivery instructions
              </label>
              <textarea
                id="delivery-instructions"
                value={deliveryInstructions}
                onChange={(event) => setDeliveryInstructions(event.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary"
              />
            </div>
          </section>

          <div className="space-y-3">
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5"
                checked={acknowledgedLimits}
                onChange={(event) => setAcknowledgedLimits(event.target.checked)}
                required
              />
              <span>I understand that there are limits on the maximum quantities available for newly released resources.</span>
            </label>
          </div>

          {error && <p className="text-sm text-error">{error}</p>}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Place Order'}
          </Button>
        </form>
      )}
    </div>
  );
}
